import React from 'react';
import { useNavigate } from 'react-router-dom';

const TranslationRequestCard = ({ data, translationRequestId, licenceId }) => {
  const navigate = useNavigate();

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { 
        day: 'numeric', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch (e) {
      return dateString;
    }
  };

  // Format language locale (e.g., "US - English" -> "US-EN")
  const formatLanguageTag = (lang) => {
    if (!lang) return '';
    if (typeof lang === 'string') {
      // If it's already formatted like "US-EN", return as is
      if (lang.includes('-') && lang.length <= 6) {
        return lang.toUpperCase();
      }
      // If it's "Country - Language", extract country and language codes
      if (lang.includes(' - ')) {
        const [country, language] = lang.split(' - ');
        const countryCode = country.substring(0, 2).toUpperCase();
        const langCode = language.substring(0, 2).toUpperCase();
        return `${countryCode}-${langCode}`;
      }
      return lang;
    }
    // If it's an object with locale, country, language
    if (lang.locale) {
      return lang.locale;
    }
    if (lang.country && lang.language) {
      const countryCode = lang.country.substring(0, 2).toUpperCase();
      const langCode = lang.language.substring(0, 2).toUpperCase();
      return `${countryCode}-${langCode}`;
    }
    return '';
  };

  const selectedLanguages = data.selectedLanguages || data.translations || [];
  const additionalRequirement = data.additionalRequirement || data.additionalRequirements || '';
  const isUrgent = data.isUrgent || false;
  const customTranslationsText = data.customTranslationsText || '';
  const territory = data.territory || '';

  const handleViewDetails = () => {
    if (translationRequestId) {
      navigate(`/translation-request/${translationRequestId}`);
    } else if (data.onViewDetails) {
      data.onViewDetails();
    } else if (licenceId) {
      navigate(`/licence-details/${licenceId}`);
    }
  };

  return (
    <div className="bg-[#F5F3FF] border border-[#E9E5FF] rounded-lg p-5 max-w-[600px] shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#8B5CF6" strokeWidth="1.5">
            <circle cx="8" cy="8" r="6.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M2 8H14M8 2C9.5 4 10.5 6 10.5 8C10.5 10 9.5 12 8 14M8 2C6.5 4 5.5 6 5.5 8C5.5 10 6.5 12 8 14" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="text-sm font-semibold text-gray-900">Request Translation</span>
        </div>
        {isUrgent && (
          <span className="px-3 py-1 text-xs font-medium bg-[#F3E8FF] text-[#8B5CF6] rounded-full border border-[#E9D5FF]">
            Urgent request
          </span>
        )}
      </div>

      {/* Translations */}
      {selectedLanguages.length > 0 && (
        <div className="mb-4">
          <div className="flex items-start gap-2 mb-2">
            <span className="text-sm text-gray-600 font-medium">Translations:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedLanguages.map((lang, index) => {
              const tag = formatLanguageTag(lang);
              if (!tag) return null;
              return (
                <span 
                  key={index}
                  className="px-3 py-1 text-xs font-medium bg-white text-gray-700 rounded-md border border-[#E9D5FF]"
                >
                  {tag}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Custom Translations Text */}
      {customTranslationsText && (
        <div className="mb-4">
          <div className="flex items-start gap-2 mb-2">
            <span className="text-sm text-gray-600 font-medium">Custom Translations Request:</span>
          </div>
          <p className="text-sm text-gray-900 bg-white rounded-md p-3 border border-[#E9D5FF]">
            {customTranslationsText}
          </p>
        </div>
      )}

      {/* Territory */}
      {territory && (
        <div className="mb-4">
          <div className="flex items-start gap-2 mb-2">
            <span className="text-sm text-gray-600 font-medium">Territory:</span>
          </div>
          <p className="text-sm text-gray-900 bg-white rounded-md p-3 border border-[#E9D5FF]">
            {territory}
          </p>
        </div>
      )}

      {/* Additional Requirements */}
      {additionalRequirement && (
        <div className="mb-4">
          <div className="flex items-start gap-2 mb-2">
            <span className="text-sm text-gray-600 font-medium">Additional Requirements:</span>
          </div>
          <p className="text-sm text-gray-900 bg-white rounded-md p-3 border border-[#E9D5FF]">
            {additionalRequirement}
          </p>
        </div>
      )}

      {/* View Details Button */}
      <button 
        onClick={handleViewDetails}
        className="text-sm font-medium text-[#8B5CF6] hover:text-[#7C3AED] transition-colors mt-4"
      >
        View Details
      </button>
    </div>
  );
};

export default TranslationRequestCard;
